import { HierarchyDropdownItemModel } from './models/hierarchy-dropdown-item.model';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ChangeDetectionStrategy, ViewChild, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { HierarchyDropdownService } from 'shared/components/hierarchy-dropdown/hierarchy-dropdown.service';
const MAXIMUM_ITEM_HIERARCHY_CYCLES_COUNT = 3;

@Component({
  selector: 'app-hierarchy-dropdown',
  templateUrl: './hierarchy-dropdown.component.html',
  styleUrls: ['./hierarchy-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class HierarchyDropdownComponent<T> implements OnInit, OnChanges {
  @Input()
  items: T[];
  @Input()
  placeholder: string;
  @Input()
  idPropertyName: string;
  @Input()
  valuePropertyName: string;
  @Input()
  parentIdPropertyName: string;
  @Input()
  textPropertyName: string;
  @Input()
  sortFieldsByText: boolean;
  @Input()
  isParentItemPropertyName: boolean;
  @Input()
  value: string;
  @Input()
  iconNameForDropdown: string;
  @Input()
  disabled: boolean = false;

  @Output()
  onItemSelected: EventEmitter<T> = new EventEmitter();

  @Output()
  onDropdownToggled: EventEmitter<any> = new EventEmitter();

  @ViewChild('menuItem', {static: true}) menu: MatMenu;

  @ViewChild(MatMenuTrigger, {static: true}) trigger: MatMenuTrigger;

  public modelItems: HierarchyDropdownItemModel[];
  public rootItems: HierarchyDropdownItemModel[];
  public selectedItem: HierarchyDropdownItemModel;

  private _initialized: boolean = false;

  constructor(private _hierarchyDropdownService: HierarchyDropdownService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (this._initialized && changes.items) {
      if (changes.items.currentValue !== changes.items.previousValue) {
        this._buildItemHierarchy();

        if (this.sortFieldsByText) {
          this._hierarchyDropdownService.sortItemsByText({ children: this.rootItems });
        }
      }
    }
    this._onModelChange();
  }

  ngOnInit() {
    this._buildItemHierarchy();

    if (this.sortFieldsByText) {
      this._hierarchyDropdownService.sortItemsByText({ children: this.rootItems });
    }

    this._onModelChange();
    this._initialized = true;
  }

  public onItemSelect(item: HierarchyDropdownItemModel): void {
    if (item.value === undefined) {
      return;
    }

    this.selectedItem = item;

    this.onItemSelected.emit(this.selectedItem.model);
    if (this.trigger && this.trigger.menuOpen) {
      this.trigger.closeMenu();
    }
  }

  public onDropdownToggle(status): void {
    this.onDropdownToggled.emit(status);
  }

  private _buildItemHierarchy(): void {
    if (!this.items) {
      return;
    }

    const idPropertyName = this.idPropertyName || 'id';
    const modelItemPropertyNameDescriptor = {
      idPropertyName,
      valuePropertyName: this.valuePropertyName || idPropertyName,
      parentIdPropertyName: this.parentIdPropertyName || 'parentId',
      textPropertyName: this.textPropertyName || 'text',
      isParentItemPropertyName: this.isParentItemPropertyName || 'isParent'
    };

    this.modelItems = this.items.map(item => new HierarchyDropdownItemModel(item, modelItemPropertyNameDescriptor));

    this.rootItems = this.modelItems.filter(item => {
      if (!item.parentId) {
        return true;
      } else {
        const isParentInModelItems = this.modelItems.find(modelItem => modelItem.id === item.parentId);
        if (!isParentInModelItems) {
          return true;
        }
      }
      return false;
    });

    const itemsToBuildList = this.modelItems.filter(item => !!item.parentId);

    let cycle = 0;
    while (itemsToBuildList.length) {
      const itemsToRemoveList = [];

      for (let i = 0, len = itemsToBuildList.length; i < len; i++) {
        const itemToBuild = itemsToBuildList[i];
        const parent = this.modelItems.find(item => item.id === itemToBuild.parentId);

        if (parent) {
          parent.setAsParentItem();
          parent.addChild(itemToBuild);
          itemsToRemoveList.push(itemToBuild);
        }
      }

      for (const itemToRemove of itemsToRemoveList) {
        itemsToBuildList.splice(itemsToBuildList.indexOf(itemToRemove), 1);
      }

      cycle++;

      if (cycle >= MAXIMUM_ITEM_HIERARCHY_CYCLES_COUNT) {
        return;
      }
    }
  }

  private _onModelChange(): void {
    if (this.modelItems) {
      this.selectedItem = this.modelItems.find(item => item.value === (this.value ? this.value : null));
    } else {
      this.selectedItem = null;
    }
  }
}
